/**
 * Export Utilities
 * Common functions for exporting data to Excel and PDF formats
 */

import axios from 'axios';
import { normalizeFilters } from '../features/devices/utils/filterApi';
import { normalizeComplaintFilters } from '../features/complaints/utils/complaintFilterApi';
import { API_ENDPOINTS } from '../config/apiConfig';

/**
 * POST filtered export — server builds file from full filtered dataset.
 */
export const exportFilteredPost = async ({
  endpoint,
  token,
  filters = {},
  sorting = null,
  columns = null,
  defaultFilename,
}) => {
  const body = { filters, export_mode: 'full' };
  if (sorting && sorting.sort_by) {
    body.sorting = {
      sort_by: sorting.sort_by,
      sort_direction: sorting.sort_direction === 'desc' ? 'desc' : 'asc',
    };
  }
  if (columns && columns.length > 0) {
    body.columns = columns;
  }

  let response;
  try {
    response = await axios.post(endpoint, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      responseType: 'blob',
    });
  } catch (error) {
    let message = `Export failed with status ${error.response?.status || 'unknown'}`;
    if (error.response?.data) {
      try {
        const text = await error.response.data.text();
        const err = JSON.parse(text);
        message = err.error || err.message || message;
      } catch {
        // blob may not be JSON
      }
    } else if (error.message) {
      message = error.message;
    }
    throw new Error(message);
  }

  const rowCountHeader = response.headers['x-export-row-count'];
  const exportedRowCount = rowCountHeader ? parseInt(rowCountHeader, 10) : null;

  const contentDisposition = response.headers['content-disposition'];
  let filename = defaultFilename;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '').trim();
    }
  }

  const blob = response.data;
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);

  return { exportedRowCount, filename };
};

export const exportFilteredDevices = async (filters, sorting, format, token, columns = null) => {
  const endpoint =
    format === 'excel' ? API_ENDPOINTS.EXPORT.DEVICES_EXCEL : API_ENDPOINTS.EXPORT.DEVICES_PDF;
  const defaultFilename = `devices_${new Date().toISOString().slice(0, 10)}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
  return exportFilteredPost({
    endpoint,
    token,
    filters: normalizeFilters(filters),
    sorting,
    columns,
    defaultFilename,
  });
};

export const exportFilteredComplaints = async (filters, sorting, format, token, columns = null) => {
  const endpoint =
    format === 'excel' ? API_ENDPOINTS.EXPORT.COMPLAINTS_EXCEL : API_ENDPOINTS.EXPORT.COMPLAINTS_PDF;
  const defaultFilename = `complaints_${new Date().toISOString().slice(0, 10)}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
  return exportFilteredPost({
    endpoint,
    token,
    filters: normalizeComplaintFilters(filters),
    sorting,
    columns,
    defaultFilename,
  });
};

export const exportFilteredAuditTrail = async (filters, token, columns = null) => {
  const defaultFilename = `audit_trail_${new Date().toISOString().slice(0, 10)}.xlsx`;
  return exportFilteredPost({
    endpoint: API_ENDPOINTS.AUDIT_VIEWER.TRANSACTIONS_EXPORT_EXCEL,
    token,
    filters,
    columns,
    defaultFilename,
  });
};

export const exportFilteredMaintenanceHistory = async (params, format, token, columns = null) => {
  const endpoint =
    format === 'excel' ? API_ENDPOINTS.EXPORT.MAINTENANCE_EXCEL : API_ENDPOINTS.EXPORT.MAINTENANCE_PDF;
  const defaultFilename = `maintenance_${new Date().toISOString().slice(0, 10)}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
  const { sort_by, sort_direction, page, limit, per_page, ...filterParams } = params || {};
  return exportFilteredPost({
    endpoint,
    token,
    filters: filterParams,
    sorting: sort_by
      ? { sort_by, sort_direction: sort_direction || 'desc' }
      : null,
    columns,
    defaultFilename,
  });
};

export const exportFilteredMyWorkLogs = async (params, format, token, columns = null) => {
  const endpoint =
    format === 'excel' ? API_ENDPOINTS.EXPORT.MY_WORK_LOGS_EXCEL : API_ENDPOINTS.EXPORT.MY_WORK_LOGS_PDF;
  const defaultFilename = `my_work_logs_${new Date().toISOString().slice(0, 10)}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
  const { sort_by, sort_direction, page, limit, per_page, ...filterParams } = params || {};
  return exportFilteredPost({
    endpoint,
    token,
    filters: filterParams,
    sorting: sort_by
      ? { sort_by, sort_direction: sort_direction || 'desc' }
      : null,
    columns,
    defaultFilename,
  });
};

export const exportFilteredActivityLog = async (params, format, token, columns = null) => {
  const defaultFilename = `activity_log_${new Date().toISOString().slice(0, 10)}.xlsx`;
  const { sort_by, sort_direction, page, limit, per_page, ...filterParams } = params || {};
  return exportFilteredPost({
    endpoint: API_ENDPOINTS.ACTIVITY_LOG.EXPORT_EXCEL,
    token,
    filters: filterParams,
    sorting: (sort_by || sort_direction)
      ? {
          sort_by: sort_by || 'timestamp',
          sort_direction: sort_direction || 'desc',
        }
      : null,
    columns,
    defaultFilename,
  });
};

/**
 * Build query parameters from filter object
 * @param {Object} filters - Filter object with various filter properties
 * @param {Array<string>} filterKeys - Keys to extract from filters object
 * @returns {URLSearchParams} Query parameters
 */
export const buildExportParams = (filters, filterKeys = []) => {
  const params = new URLSearchParams();

  filterKeys.forEach(key => {
    const value = filters[key];
    
    if (!value) return;

    // Handle array values (multi-select filters)
    if (Array.isArray(value)) {
      value.forEach(item => {
        params.append(key, item);
      });
    }
    // Handle string values
    else if (typeof value === 'string' && value.trim()) {
      params.append(key, value);
    }
  });

  return params;
};

/**
 * Generic export handler for both Excel and PDF
 * @param {string} endpoint - Full API endpoint URL from API_ENDPOINTS
 * @param {string} token - Authorization token
 * @param {URLSearchParams} params - Query parameters
 * @param {string} defaultFilename - Default filename if not provided by server
 * @returns {Promise<void>}
 */
export const handleExport = async (endpoint, token, params, defaultFilename) => {
  try {
    const queryString = params.toString();
    const url = `${endpoint}${queryString ? '?' + queryString : ''}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Export failed with status ${response.status}`);
    }

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = defaultFilename;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '').trim();
      }
    }

    // Download the file
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error during export:', error);
    throw error;
  }
};

/**
 * Export complaints with filters
 * @param {Object} filters - Complaint filters object
 * @param {string} format - Export format ('excel' or 'pdf')
 * @param {string} token - Authorization token
 * @returns {Promise<void>}
 */
export const exportComplaints = async (filters, format, token) => {
  return exportFilteredComplaints(filters, null, format, token);
};

/**
 * Export devices with filters
 * @param {Object} filters - Device filters object
 * @param {string} format - Export format ('excel' or 'pdf')
 * @param {string} token - Authorization token
 * @returns {Promise<void>}
 */
export const exportDevices = async (filters, format, token) => {
  return exportFilteredDevices(filters, null, format, token);
};

/**
 * Export maintenance logs with filters
 * @param {Object} filters - Device filters object (maintenance uses device filters)
 * @param {string} token - Authorization token
 * @returns {Promise<void>}
 */
export const exportMaintenance = async (filters, token) => {
  return exportFilteredMaintenanceHistory(filters || {}, 'pdf', token);
};

/**
 * Create export filter object from complaint filter state
 * @param {Array<string>} statusFilter - Selected statuses
 * @param {Array<string>} severityFilter - Selected severities
 * @param {Array<string>} departmentFilter - Selected departments
 * @param {string} searchTerm - Search term
 * @param {Object} dateRange - Date range object with startDate and endDate
 * @returns {Object} Filter object for export
 */
export const createComplaintExportFilters = (
  statusFilter,
  severityFilter,
  departmentFilter,
  searchTerm,
  dateRange
) => {
  return {
    statuses: statusFilter,
    severities: severityFilter,
    departments: departmentFilter,
    search: searchTerm,
    date_from: dateRange.startDate,
    date_to: dateRange.endDate
  };
};

/**
 * Create export filter object from device filter state
 * @param {Object} filters - Device filters object from App state
 * @returns {Object} Filter object for export
 */
export const createDeviceExportFilters = (filters) => {
  return {
    departments: filters.departments,
    search: filters.search,
    overdue_maintenance: filters.overdueMaintenanceOnly ? 'true' : '',
    overdue_calibration: filters.overdueCalibrationOnly ? 'true' : '',
    expired_warranty: filters.expiredWarrantyOnly ? 'true' : '',
    expired_contract: filters.expiredContractOnly ? 'true' : '',
    has_complaints: filters.hasComplaintsOnly ? 'true' : '',
    installation_date_from: filters.installationDateFrom,
    installation_date_to: filters.installationDateTo,
    warranty_date_from: filters.warrantyDateFrom,
    warranty_date_to: filters.warrantyDateTo,
    contract_date_from: filters.contractDateFrom,
    contract_date_to: filters.contractDateTo
  };
};

/**
 * Generate bulk QR code PDF for multiple devices
 * Creates an A4 page with 4x4 grid of QR codes (16 per page)
 * @param {Array<Object>} devices - Array of device objects
 * @param {Function} generateQRCodeDataURL - Function that takes a device and returns QR code data URL
 * @returns {Promise<void>}
 */
export const generateBulkQRCodesPDF = async (devices, generateQRCodeDataURL) => {
  try {
    const { jsPDF } = await import('jspdf');

    if (!devices || devices.length === 0) {
      throw new Error('No devices selected for QR code generation');
    }

    // A4 dimensions in mm
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 5;
    const contentWidth = pageWidth - (2 * margin);
    const contentHeight = pageHeight - (2 * margin);

    // 4x4 grid layout (16 QR codes per page)
    const cols = 4;
    const rows = 4;
    const cellWidth = contentWidth / cols;
    const cellHeight = contentHeight / rows;

    // QR code dimensions within each cell
    const qrSizeMM = 30; // mm - slightly smaller than the 35mm cell allocation
    const fontSize = 6;
    const lineHeight = 2.2;

    // Initialize PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'A4'
    });

    let deviceIndex = 0;
    let pageNumber = 0;

    while (deviceIndex < devices.length) {
      if (pageNumber > 0) {
        pdf.addPage();
      }

      // Draw grid for current page
      for (let row = 0; row < rows && deviceIndex < devices.length; row++) {
        for (let col = 0; col < cols && deviceIndex < devices.length; col++) {
          const device = devices[deviceIndex];
          const x = margin + (col * cellWidth);
          const y = margin + (row * cellHeight);

          // Draw cell border
          pdf.setDrawColor(200);
          pdf.setLineWidth(0.3);
          pdf.rect(x, y, cellWidth, cellHeight);

          // Device ID at top
          pdf.setFont('courier', 'bold');
          pdf.setFontSize(5.5);
          const deviceId = device.device_identifier || device.device_id || 'N/A';
          pdf.text(deviceId, x + (cellWidth / 2), y + 2.5, { align: 'center' });

          // Generate and embed QR Code
          try {
            const qrCodeDataURL = await generateQRCodeDataURL(device);
            if (qrCodeDataURL) {
              const qrX = x + (cellWidth - qrSizeMM) / 2;
              const qrY = y + 5;
              pdf.addImage(qrCodeDataURL, 'PNG', qrX, qrY, qrSizeMM, qrSizeMM);
            }
          } catch (qrError) {
            console.warn(`Failed to generate QR code for device ${deviceId}:`, qrError);
            // Draw placeholder if QR generation fails
            pdf.setFillColor(240, 240, 240);
            const qrX = x + (cellWidth - qrSizeMM) / 2;
            const qrY = y + 5;
            pdf.rect(qrX, qrY, qrSizeMM, qrSizeMM, 'F');
            pdf.setDrawColor(100);
            pdf.rect(qrX, qrY, qrSizeMM, qrSizeMM);
          }

          // Device info below QR code
          const infoY = y + 37; // Position below QR code
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(fontSize);

          // Equipment
          pdf.setFont('helvetica', 'bold');
          pdf.text('Eq:', x + 1, infoY);
          pdf.setFont('helvetica', 'normal');
          const equipmentText = (device.name || 'N/A').substring(0, 12);
          pdf.text(equipmentText, x + 9, infoY);

          // Dept
          pdf.setFont('helvetica', 'bold');
          pdf.text('D:', x + 1, infoY + lineHeight);
          pdf.setFont('helvetica', 'normal');
          const deptText = (device.department || 'N/A').substring(0, 13);
          pdf.text(deptText, x + 9, infoY + lineHeight);

          // Model
          pdf.setFont('helvetica', 'bold');
          pdf.text('M:', x + 1, infoY + (lineHeight * 2));
          pdf.setFont('helvetica', 'normal');
          const modelText = (device.model || 'N/A').substring(0, 13);
          pdf.text(modelText, x + 9, infoY + (lineHeight * 2));

          // SN
          pdf.setFont('helvetica', 'bold');
          pdf.text('SN:', x + 1, infoY + (lineHeight * 3));
          pdf.setFont('helvetica', 'normal');
          const snText = (device.serial_number || 'N/A').substring(0, 11);
          pdf.text(snText, x + 9, infoY + (lineHeight * 3));

          deviceIndex++;
        }
      }

      pageNumber++;
    }

    // Download PDF
    const timestamp = new Date().toISOString().slice(0, 10);
    const filename = `QR_Codes_${devices.length}_devices_${timestamp}.pdf`;
    pdf.save(filename);
  } catch (error) {
    console.error('Error generating bulk QR codes PDF:', error);
    throw error;
  }
};

/**
 * Client-side Excel export for data arrays
 * @param {Array<Object>} data - Data to export as rows
 * @param {string} filename - Output filename
 * @param {Object} options - Export options (columnWidths, sheetName, headerStyle)
 * @returns {Promise<void>}
 */
export const exportToExcelClient = async (data, filename = 'export.xlsx', options = {}) => {
  try {
    // Dynamically import ExcelJS for proper styling support
    const { Workbook } = await import('exceljs');
    const { getExportHeaderFillHex } = await import('../shared/utils/exportTheme');

    if (!data || data.length === 0) {
      console.warn('No data to export');
      return;
    }

    // Create workbook and worksheet
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(options.sheetName || 'Data');

    const headers = Object.keys(data[0]);
    const meta = options.meta || null;
    let headerRowNumber = 1;
    const headerFillHex = `FF${getExportHeaderFillHex()}`;

    if (meta) {
      const titleRow = worksheet.addRow([meta.title || 'Export']);
      titleRow.font = { name: 'Open Sans', bold: true, size: 14, color: { argb: 'FF1F2937' } };
      worksheet.mergeCells(1, 1, 1, Math.max(headers.length, 1));

      const metaParts = [];
      if (meta.organizationName) metaParts.push(`Organization: ${meta.organizationName}`);
      if (meta.locationLabel) metaParts.push(`Location: ${meta.locationLabel}`);
      if (meta.exportedAt) metaParts.push(`Exported: ${meta.exportedAt}`);
      const metaRow = worksheet.addRow([metaParts.join('  |  ')]);
      metaRow.font = { name: 'Open Sans', size: 10, color: { argb: 'FF4B5563' } };
      worksheet.mergeCells(2, 1, 2, Math.max(headers.length, 1));

      const totalLabel = meta.rowCountLabel || 'Total';
      const totalValue = meta.rowCount != null ? meta.rowCount : data.length;
      const totalRow = worksheet.addRow([`${totalLabel}: ${totalValue}`]);
      totalRow.font = { name: 'Open Sans', size: 10, color: { argb: 'FF1F2937' } };
      worksheet.mergeCells(3, 1, 3, Math.max(headers.length, 1));

      worksheet.addRow([]); // spacer
      headerRowNumber = 5;
    }

    // Add header row
    const headerRow = worksheet.addRow(headers);

    // Style header row (org CTA accent with white text)
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: headerFillHex },
      };
      cell.font = {
        name: 'Open Sans',
        bold: true,
        color: { argb: 'FFFFFFFF' },
        size: 12,
      };
      cell.alignment = {
        vertical: 'center',
        wrapText: false,
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
      };
    });

    // Set header row height for better vertical centering visibility
    headerRow.height = 30;

    // Add data rows — Open Sans, light grid; no wrap (widen columns instead)
    data.forEach((row, rowIndex) => {
      const dataRow = worksheet.addRow(Object.values(row));
      dataRow.eachCell((cell) => {
        cell.font = { name: 'Open Sans', size: 10, color: { argb: 'FF1F2937' } };
        cell.alignment = { vertical: 'middle', wrapText: false };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
          bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
          left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
          right: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        };
        if (rowIndex % 2 === 1) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF5F5F5' },
          };
        }
      });
    });

    // Set column widths from content (single-line cells)
    if (options.columnWidths && options.columnWidths.length > 0) {
      headers.forEach((header, index) => {
        const colWidth = options.columnWidths[index];
        if (colWidth && colWidth.wch) {
          worksheet.getColumn(index + 1).width = colWidth.wch;
        }
      });
    } else {
      headers.forEach((header, index) => {
        let maxLen = String(header || '').length;
        data.forEach((row) => {
          const value = row[header];
          const len = value == null ? 0 : String(value).length;
          if (len > maxLen) maxLen = len;
        });
        // Excel width ≈ character count; cap so wide emails don't dominate
        worksheet.getColumn(index + 1).width = Math.min(48, Math.max(12, maxLen + 2));
      });
    }

    worksheet.views = [{ state: 'frozen', ySplit: headerRowNumber }];

    // Generate and download file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw error;
  }
};
